import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { PublicLayout } from "@/components/PublicLayout";
import { SectionHeading } from "@/components/SectionHeading";
import { Seo } from "@/components/Seo";
import { BUSINESS, SERVICES } from "@/data/site";
import api from "@/lib/api";

const empty = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(empty);
      toast.success("Your quote request has been sent!");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <Seo title="Contact & Free Quote" description="Request a free, no-obligation construction quote from Crawford Construction & Development Ltd. in Placencia Village, Belize. Call +501-639-5057 or message us on WhatsApp." />
      <section className="pt-40 pb-16 bg-navy">
        <div className="max-w-7xl mx-auto px-5">
          <p className="text-xs sm:text-sm tracking-[0.25em] uppercase font-bold text-gold mb-5">Get In Touch</p>
          <h1 className="font-display text-5xl sm:text-6xl text-white leading-tight">Request a quote.</h1>
          <p className="mt-6 text-white/70 text-lg max-w-2xl">Tell us about your project and we'll get back to you with a free, no-obligation estimate.</p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-5 gap-12">
          {/* Info */}
          <div className="lg:col-span-2 space-y-8">
            <SectionHeading overline="Contact Details" title="Let's build together." />
            <div className="space-y-6">
              <a href={BUSINESS.phoneHref} className="flex gap-4 group" data-testid="contact-phone">
                <div className="h-12 w-12 flex items-center justify-center bg-navy text-gold group-hover:bg-gold group-hover:text-navy transition-colors shrink-0"><Phone size={20} /></div>
                <div><div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Phone</div><div className="text-navy font-semibold">{BUSINESS.phone}</div></div>
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="flex gap-4 group" data-testid="contact-email">
                <div className="h-12 w-12 flex items-center justify-center bg-navy text-gold group-hover:bg-gold group-hover:text-navy transition-colors shrink-0"><Mail size={20} /></div>
                <div><div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Email</div><div className="text-navy font-semibold break-all">{BUSINESS.email}</div></div>
              </a>
              <div className="flex gap-4">
                <div className="h-12 w-12 flex items-center justify-center bg-navy text-gold shrink-0"><MapPin size={20} /></div>
                <div><div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Location</div><div className="text-navy font-semibold">{BUSINESS.address}</div></div>
              </div>
            </div>

            <a
              href={`https://wa.me/5016395057?text=${encodeURIComponent("Hi Crawford Construction, I'd like to request a quote for my project.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="flex items-center justify-center gap-2 bg-[#25D366] text-white py-3.5 font-bold tracking-wide hover:brightness-95 transition-all"
            >
              <MessageCircle size={18} /> Get a Quote on WhatsApp
            </a>

            <div className="border border-gray-200 overflow-hidden" data-testid="contact-map">
              <iframe
                title="Crawford Construction location — Placencia Village, Belize"
                src="https://www.google.com/maps?q=Placencia%20Village%2C%20Stann%20Creek%20District%2C%20Belize&output=embed"
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-gold/40 p-12 text-center" data-testid="quote-success">
                <CheckCircle2 size={56} className="text-gold mx-auto" />
                <h3 className="font-display text-3xl text-navy mt-6">Thank you!</h3>
                <p className="mt-3 text-muted-foreground">Your quote request has been received. We'll be in touch shortly.</p>
                <button onClick={() => setDone(false)} className="mt-8 border border-navy text-navy px-6 py-3 font-semibold hover:bg-navy hover:text-white transition-colors">Send another request</button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="bg-white border border-gray-100 p-8 sm:p-10 space-y-6 shadow-sm">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Full Name *"><input data-testid="input-name" value={form.name} onChange={update("name")} className="cc-input" placeholder="John Crawford" /></Field>
                  <Field label="Email *"><input data-testid="input-email" type="email" value={form.email} onChange={update("email")} className="cc-input" placeholder="you@email.com" /></Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Phone"><input data-testid="input-phone" value={form.phone} onChange={update("phone")} className="cc-input" placeholder="+501 ..." /></Field>
                  <Field label="Service Needed">
                    <select data-testid="input-service" value={form.service} onChange={update("service")} className="cc-input">
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => <option key={s.key} value={s.title}>{s.title}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Project Details *">
                  <textarea data-testid="input-message" value={form.message} onChange={update("message")} rows={5} className="cc-input resize-none" placeholder="Tell us about your project, location, and timeline..." />
                </Field>
                <button type="submit" disabled={submitting} data-testid="submit-quote-btn" className="w-full flex items-center justify-center gap-2 bg-navy text-white py-4 font-bold tracking-wide hover:bg-navy-light transition-colors disabled:opacity-60 border border-transparent hover:border-gold">
                  {submitting ? "Sending..." : <>Send Request <Send size={17} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`.cc-input{width:100%;border:1px solid #E5E7EB;padding:0.75rem 1rem;background:#fff;color:#0A1220;font-size:0.95rem;outline:none;transition:border-color .2s}.cc-input:focus{border-color:#D4AF37;box-shadow:0 0 0 1px #D4AF37}`}</style>
    </PublicLayout>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-widest font-semibold text-navy mb-2">{label}</span>
    {children}
  </label>
);
